// Radar Chart Component - FIFA Style (Fixed PHY visibility)
import { useEffect, useRef } from "react";

const statLabels = {
  vitesse: "VIT",
  tir: "TIR",
  passe: "PAS",
  dribble: "DRI",
  defense: "DEF",
  physique: "PHY",
};

const statOrder = ["vitesse", "tir", "passe", "dribble", "defense", "physique"];

export default function RadarChart({ stats, size = 180 }) {
  const canvasRef = useRef(null);
  
  // Extra padding for labels + values (prevents PHY clipping at bottom)
  const padding = 44;
  const canvasSize = size + (padding * 2);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const centerX = canvasSize / 2;
    const centerY = canvasSize / 2;
    const radius = size / 2 - 10;
    const numPoints = 6;
    const angleStep = (Math.PI * 2) / numPoints;

    // Clear canvas
    ctx.clearRect(0, 0, canvasSize, canvasSize);

    // Draw background hexagons (grid lines)
    const gridLevels = [0.2, 0.4, 0.6, 0.8, 1];
    gridLevels.forEach((level) => {
      ctx.beginPath();
      for (let i = 0; i < numPoints; i++) {
        const angle = i * angleStep - Math.PI / 2;
        const x = centerX + Math.cos(angle) * radius * level;
        const y = centerY + Math.sin(angle) * radius * level;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.strokeStyle = "rgba(255, 255, 255, 0.1)";
      ctx.lineWidth = 1;
      ctx.stroke();
    });

    // Draw axis lines
    for (let i = 0; i < numPoints; i++) {
      const angle = i * angleStep - Math.PI / 2;
      const x = centerX + Math.cos(angle) * radius;
      const y = centerY + Math.sin(angle) * radius;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(x, y);
      ctx.strokeStyle = "rgba(255, 255, 255, 0.1)";
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // Draw stat polygon
    ctx.beginPath();
    statOrder.forEach((stat, i) => {
      const value = (stats[stat] || 0) / 100;
      const angle = i * angleStep - Math.PI / 2;
      const x = centerX + Math.cos(angle) * radius * value;
      const y = centerY + Math.sin(angle) * radius * value;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();

    // Fill with gradient
    const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
    gradient.addColorStop(0, "rgba(34, 197, 94, 0.4)");
    gradient.addColorStop(1, "rgba(34, 197, 94, 0.1)");
    ctx.fillStyle = gradient;
    ctx.fill();

    // Stroke
    ctx.strokeStyle = "#22c55e";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw points
    statOrder.forEach((stat, i) => {
      const value = (stats[stat] || 0) / 100;
      const angle = i * angleStep - Math.PI / 2;
      const x = centerX + Math.cos(angle) * radius * value;
      const y = centerY + Math.sin(angle) * radius * value;
      
      ctx.beginPath();
      ctx.arc(x, y, 4, 0, Math.PI * 2);
      ctx.fillStyle = "#22c55e";
      ctx.fill();
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2;
      ctx.stroke();
    });

    // Draw labels with generous padding to prevent clipping
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    statOrder.forEach((stat, i) => {
      const angle = i * angleStep - Math.PI / 2;
      const labelRadius = radius + 26;
      const x = centerX + Math.cos(angle) * labelRadius;
      const y = centerY + Math.sin(angle) * labelRadius;
      
      // Label
      ctx.font = "bold 11px Inter, sans-serif";
      ctx.fillStyle = "#9ca3af";
      ctx.fillText(statLabels[stat], x, y);
      
      // Value below label
      const value = stats[stat] || 0;
      ctx.font = "bold 10px Inter, sans-serif";
      ctx.fillStyle = "#22c55e";
      ctx.fillText(value.toString(), x, y + 13);
    });

  }, [stats, size, canvasSize]);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
      <canvas
        ref={canvasRef}
        width={canvasSize}
        height={canvasSize}
        data-testid="radar-chart"
        style={{ maxWidth: '100%' }}
      />
    </div>
  );
}
